import Class from "./Class";
import Mapping from "./Mapping";
import Container from "./Container";
import AVLTree from "./AVLTree";
import sorting from "./Sorting";
import number from "./formats/number";
import timestamp from "./formats/timestamp";

const parseNumber = number.parse;

const Combiner = new Class({
    columns: function() {
        return [];
    },

    header: function(column) {
        return column;
    },

    map: function(item) {
        return [null];
    },

    init: function(value) {
        return value;
    },

    insert: function(value, combined) {
        return combined;
    },

    remove: function(value, combined) {
        return combined;
    },

    snapshot: function(combined) {
        return combined;
    },

    render: function(snapshot, column) {
        return "";
    },

    renderer: function(type, column, _extract) {
        const key = "renderer:" + type;
        const extract = _extract ? _extract : function(item) {
            return item;
        };

        if (this[key]) {
            return this[key](column, extract);
        }
        return null;
    },

    "renderer:text": function(column, extract) {
        const combiner = this;
        return {
            render: function(item) {
                return combiner.render(extract(item), column);
            }
        };
    },

    cmpColumns: function(left, right) {
        return 0;
    },

    cmpKeys: function(left, right) {
        return 0;
    },

    cmpValues: function(left, right, _) {
        return 0;
    }
});


// Dummy

const Dummy = new Class({
    Extends: Combiner
});


// Sum

const Sum = new Class({
    Extends: Combiner,

    initialize: function(key, options) {
        Combiner.call(this);

        this._key = key;
        this._header = String(Container.get(options, "header", "sum\u00a0of " + key));
        this._required = Boolean(Container.get(options, "required", true));
    },

    columns: function() {
        return [this._header];
    },

    map: function(item) {
        const values = item.event.values(this._key, parseNumber);
        if (values.length === 0) {
            return this._required ? [] : [null];
        }
        return [values];
    },

    _calc: function(values, _combined, sign) {
        let combined = _combined;
        if (values === null) {
            return combined;
        }

        // Instead of naively summing up the values use Kahan Summation
        // to improve accuracy lost due to floating point arithmetic.

        if (combined === null) {
            combined = {
                "count": 0,
                "sum": 0.0,
                "correction": 0.0
            };
        }

        const count = combined.count + sign;
        if (count <= 0) {
            return null;
        }

        let s = combined.sum;
        let c = combined.correction;
        for (let i = 0, len = values.length; i < len; i++) {
            const y = sign * values[i] - c;
            const t = s + y;
            c = (t - s) - y;
            s = t;
        }

        combined.count = count;
        combined.sum = s;
        combined.correction = c;

        return combined;
    },

    init: function(values) {
        return this._calc(values, null, 1);
    },

    insert: function(values, combined) {
        return this._calc(values, combined, 1);
    },

    remove: function(values, combined) {
        return this._calc(values, combined, -1);
    },

    snapshot: function(combined) {
        return combined === null ? null : combined.sum;
    },

    render: function(snapshot, column) {
        if (snapshot === null) {
            return "";
        }
        return "" + snapshot;
    },

    "renderer:number": function(column, extract) {
        return !this._required ? null : {
            render: function(snapshot) {
                return extract(snapshot);
            }
        };
    },

    cmpValues: function(left, right, _) {
        if (left === null && right === null) {
            return 0;
        } else if (left === null) {
            return -1;
        } else if (right === null) {
            return 1;
        }
        return left - right;
    }
});


const _BinaryOp = new Class({
    Extends: Combiner,

    initialize: function(name, op, key, options) {
        Combiner.call(this);

        this._op = op;
        this._key = key;
        this._header = String(Container.get(options, "header", name + "\u00a0" + key));
        this._required = Boolean(Container.get(options, "required", true));
    },

    columns: function() {
        return [this._header];
    },

    map: function(item) {
        const values = item.event.values(this._key, this._op.parse);
        if (values.length === 0) {
            return this._required ? [] : [null];
        }

        let result = 0;
        for (let i = 0, len = values.length; i < len; i++) {
            result += values[i];
        }
        return [result];
    },

    init: function(value) {
        if (value === null) {
            return null;
        }

        const tree = new AVLTree();
        tree.combine = this._op.combine;
        tree.insert(value);
        return tree;
    },

    insert: function(value, tree) {
        if (value === null) {
            return tree;
        } else if (tree === null) {
            return this.init(value);
        }
        tree.insert(value);
        return tree;
    },

    remove: function(value, tree) {
        if (tree === null) {
            return null;
        } else if (value === null) {
            return tree;
        }
        tree.find(value).remove();
        return tree;
    },

    snapshot: function(tree) {
        if (tree === null) {
            return null;
        }
        return tree.combined();
    },

    render: function(snapshot, column) {
        if (snapshot === null) {
            return null;
        }
        return this._op.format(snapshot);
    },

    cmpValues: function(left, right, _) {
        if (left === null && right === null) {
            return 0;
        } else if (left === null) {
            return -1;
        } else if (right === null) {
            return 1;
        }
        return left - right;
    }
});

const Min = new Class({
    Extends: _BinaryOp,

    initialize: function(key, options) {
        _BinaryOp.call(this, "min", {
            "parse": parseNumber,
            "combine": Math.min,
            "format": String
        }, key, options);
    },

    "renderer:number": function(column, extract) {
        return !this._required ? null : {
            render: function(snapshot) {
                return extract(snapshot);
            }
        };
    }
});

const Max = new Class({
    Extends: _BinaryOp,

    initialize: function(key, options) {
        _BinaryOp.call(this, "max", {
            "parse": parseNumber,
            "combine": Math.max,
            "format": String
        }, key, options);
    },

    "renderer:number": function(column, extract) {
        return !this._required ? null : {
            render: function(snapshot) {
                return extract(snapshot);
            }
        };
    }
});

const FirstSeen = new Class({
    Extends: _BinaryOp,

    initialize: function(key, options) {
        _BinaryOp.call(this, "first", {
            "parse": timestamp.parse,
            "combine": Math.min,
            "format": timestamp.format
        }, key, options);
    }
});

const LastSeen = new Class({
    Extends: _BinaryOp,

    initialize: function(key, options) {
        _BinaryOp.call(this, "last", {
            "parse": timestamp.parse,
            "combine": Math.max,
            "format": timestamp.format
        }, key, options);
    }
});


// Unique

const Unique = new Class({
    Extends: Combiner,

    initialize: function(key, options) {
        Combiner.call(this);

        this._key = key;
        this._sort = Container.get(options, "sort", sorting.globalSort);
        this._header = String(Container.get(options, "header", key));
        this._required = Boolean(Container.get(options, "required", true));

        this._mapFilter = {
            _sort: this._sort,

            "map": function(value) {
                return this._sort.map(value);
            },
            "filter": function(value) {
                return value !== null;
            }
        };
    },

    columns: function() {
        return [this._header];
    },

    map: function(item) {
        const values = item.event.values(this._key, this._mapFilter);
        if (values.length > 0) {
            return values;
        } else if (!this._required) {
            return [null];
        }
        return [];
    },

    render: function(snapshot, column) {
        if (snapshot === null) {
            return "";
        }
        return this._sort.unmap(snapshot);
    },

    cmpKeys: function(left, right) {
        if (left === null) {
            return right === null ? 0 : -1;
        } else if (right === null) {
            return 1;
        }
        return this._sort.cmpKeys(left, right);
    },

    cmpValues: function(left, right) {
        if (left === null) {
            return right === null ? 0 : -1;
        } else if (right === null) {
            return 1;
        }
        return this._sort.cmpValues(left, right);
    }
});


// Count

const Count = new Class({
    Extends: Combiner,

    initialize: function(options) {
        Combiner.call(this);
        this._header = String(Container.get(options, "header", "# events"));
    },
    columns: function() {
        return [this._header];
    },

    map: function(item) {
        return [1];
    },

    insert: function(value, combined) {
        return combined + value;
    },

    remove: function(value, combined) {
        return combined - value;
    },

    render: function(snapshot, column) {
        return "" + snapshot;
    },

    cmpValues: function(left, right, _) {
        return left - right;
    },

    "renderer:number": function(column, extract) {
        return {
            render: function(snapshot) {
                return extract(snapshot);
            }
        };
    }
});


// Group

const Group = new Class({
    Extends: Combiner,

    initialize: function(combiners) {
        Combiner.call(this);

        const keys = [];
        if (Array.isArray(combiners)) {
            this._keyedColumns = [];

            combiners.forEach(function(combiner, index) {
                keys.push(index);
            }, this);
        } else {
            this._keyedColumns = {};

            Container.forEach(combiners, function(combiner, key) {
                keys.push(key);
            }, this);
        }

        this._combiners = keys.map(function(key) {
            return combiners[key];
        });

        keys.forEach(function(key, index) {
            this._keyedColumns[key] = combiners[key].columns().map(function(column) {
                return {
                    "index": index,
                    "column": column
                };
            });
        }, this);

        this._columns = [];
        keys.forEach(function(key, index) {
            this._keyedColumns[key].forEach(function(column) {
                this._columns.push(column);
            }, this);
        }, this);
    },

    columns: function(key) {
        if (key === void 0) {
            return this._columns.slice(0);
        } else {
            return this._keyedColumns[key].slice(0);
        }
    },

    header: function(column) {
        return this._combiners[column.index].header(column.column);
    },

    map: function(item) {
        const combiners = this._combiners;
        const length = combiners.length;
        if (length === 0) {
            return [];
        }

        let resultCount = 1;
        const mapArray = new Array(length);
        for (let i = 0; i < length && resultCount > 0; i++) {
            const mapped = combiners[i].map(item);
            resultCount *= mapped.length;
            mapArray[i] = mapped;
        }

        if (resultCount <= 0) {
            return [];
        }

        const results = new Array(resultCount);
        for (let j = 0; j < resultCount; j++) {
            results[j] = new Array(length);
        }

        let repeat = 1;
        for (let index = 0; index < length; index++) {
            const values = mapArray[index];
            const valueCount = values.length;

            let resultIndex = 0;
            while (resultIndex < resultCount) {
                for (let valueIndex = 0; valueIndex < valueCount; valueIndex++) {
                    const value = values[valueIndex];

                    for (let r = 0; r < repeat; r++) {
                        results[resultIndex++][index] = value;
                    }
                }
            }

            repeat *= valueCount;
        }
        return results;
    },

    init: function(value) {
        const combiners = this._combiners;
        const length = combiners.length;
        const result = new Array(length);
        for (let i = 0; i < length; i++) {
            result[i] = combiners[i].init(value[i]);
        }
        return result;
    },

    insert: function(value, combined) {
        const combiners = this._combiners;
        for (let i = 0, length = combiners.length; i < length; i++) {
            combined[i] = combiners[i].insert(value[i], combined[i]);
        }
        return combined;
    },

    remove: function(value, combined) {
        const combiners = this._combiners;
        for (let i = 0, length = combiners.length; i < length; i++) {
            combined[i] = combiners[i].remove(value[i], combined[i]);
        }
        return combined;
    },

    snapshot: function(combined) {
        const combiners = this._combiners;
        const length = combiners.length;
        const result = new Array(length);
        for (let i = 0; i < length; i++) {
            result[i] = combiners[i].snapshot(combined[i]);
        }
        return result;
    },

    render: function(snapshot, column) {
        const combiner = this._combiners[column.index];
        return combiner.render(snapshot[column.index], column.column);
    },

    renderer: function(type, column, _extract) {
        const index = column.index;
        return this._combiners[index].renderer(type, column.column, function(_item) {
            const item = _extract ? _extract(_item) : _item;
            return item[index];
        });
    },

    cmpColumns: function(left, right) {
        const diff = left.index - right.index;
        if (diff !== 0) {
            return diff;
        }
        return this._combiners[left.index].cmpColumns(left.column, right.column);
    },

    cmpKeys: function(left, right) {
        const combiners = this._combiners;
        for (let i = 0, len = combiners.length; i < len; i++) {
            const result = combiners[i].cmpKeys(left[i], right[i]);
            if (result !== 0) {
                return result;
            }
        }
        return 0;
    },

    cmpValues: function(left, right, column) {
        const index = column.index;
        return this._combiners[index].cmpValues(left[index], right[index], column.column);
    }
});


// Count unique

const CountUnique = new Class({
    Extends: Combiner,

    initialize: function(key, options) {
        Combiner.call(this);

        this._combiner = this._createCombiner(key);
        this._header = String(Container.get(options, "header", this._createHeader(this._combiner)));
        this._required = Boolean(Container.get(options, "required", true));

        this._comparator = {
            "_combiner": this._combiner,

            "cmp": function(left, right) {
                return this._combiner.cmpKeys(left, right);
            }
        };
    },

    _createCombiner: function(key) {
        if (typeof key === "string") {
            return new Unique(key, {
                "required": true
            });
        } else {
            return new Group(key.map(function(key) {
                return new Unique(key, {
                    "required": true
                });
            }));
        }
    },

    _createHeader: function(combiner) {
        const headers = combiner.columns().map(function(column) {
            return combiner.header(column);
        });

        if (headers.length === 1) {
            return "# " + headers[0] + " values";
        } else {
            return "# " + headers.join("/") + " combinations";
        }
    },

    columns: function() {
        return [this._header];
    },

    map: function(item) {
        const values = this._combiner.map(item);
        if (values.length === 0 && this._required) {
            return [];
        }
        return [values];
    },

    init: function(values) {
        return this.insert(values, null);
    },

    insert: function(values, counts) {
        if (counts === null) {
            counts = new Mapping(this._comparator);
        }
        for (let i = 0, len = values.length; i < len; i++) {
            const value = values[i];
            counts.set(value, counts.get(value, 0) + 1);
        }
        return counts;
    },

    remove: function(values, counts) {
        if (counts === null) {
            return null;
        }

        for (let i = 0, len = values.length; i < len; i++) {
            const value = values[i];

            const count = counts.get(value, 0);
            if (count <= 1) {
                counts.pop(value);
            } else {
                counts.set(value, count - 1);
            }
        }

        if (counts.count() > 0) {
            return counts;
        }
        return null;
    },

    snapshot: function(counts) {
        if (counts === null) {
            return 0;
        }
        return counts.count();
    },

    render: function(snapshot, _) {
        return "" + snapshot;
    },

    cmpValues: function(left, right, _) {
        return left - right;
    },

    "renderer:number": function(column, extract) {
        return {
            render: function(snapshot) {
                return extract(snapshot);
            }
        };
    }
});

export default {
    "Combiner": Combiner,
    "Dummy": Dummy,
    "Sum": Sum,
    "Min": Min,
    "Max": Max,
    "FirstSeen": FirstSeen,
    "LastSeen": LastSeen,
    "Unique": Unique,
    "Count": Count,
    "Group": Group,
    "CountUnique": CountUnique
};



// WEBPACK FOOTER //
// ./src/abusesa-js/combiners.js